import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import ReactDOM from 'react-dom';

import Button from '../../components/Button';
import { questionApi } from '../../services/questionApi';
import { Question, QuestionType } from '../../types/question';
import { useTheme } from '../../context/ThemeContext';

interface EditQuestionModalProps {
  question: Question;
  onUpdate: (updatedQuestion: Question) => void;
  onCancel: () => void;
}

const EditQuestionModal: React.FC<EditQuestionModalProps> = ({
  question,
  onUpdate,
  onCancel,
}) => {
  const { theme } = useTheme();
  
  const [questionText, setQuestionText] = useState(question.question);
  const [answerText, setAnswerText] = useState(question.answer);
  const [difficulty, setDifficulty] = useState(question.difficulty);
  const [questionType, setQuestionType] = useState(question.questionType || QuestionType.FREE);
  const [answerRange, setAnswerRange] = useState<number | undefined>(question.answerRange);

  const [questionImagePreview, setQuestionImagePreview] = useState(
    question.questionThumbnailUrl || ''
  );
  const [answerImagePreview, setAnswerImagePreview] = useState(
    question.answerThumbnailUrl || ''
  );
  const [qFile, setQFile] = useState<File | null>(null);
  const [aFile, setAFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropzone for question image
  const onDropQuestion = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file && file.size <= 500000) {
        if (questionImagePreview && questionImagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(questionImagePreview);
        }
        setQFile(file);
        setQuestionImagePreview(URL.createObjectURL(file));
      }
    },
    [questionImagePreview]
  );

  const {
    getRootProps: getQuestionRootProps,
    getInputProps: getQuestionInputProps,
    isDragActive: isQuestionDragActive,
  } = useDropzone({
    onDrop: onDropQuestion,
    accept: { 'image/*': [] },
    maxSize: 500000,
    multiple: false,
  });

  // Dropzone for answer image
  const onDropAnswer = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file && file.size <= 500000) {
        if (answerImagePreview && answerImagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(answerImagePreview);
        }
        setAFile(file);
        setAnswerImagePreview(URL.createObjectURL(file));
      }
    },
    [answerImagePreview]
  );

  const {
    getRootProps: getAnswerRootProps,
    getInputProps: getAnswerInputProps,
    isDragActive: isAnswerDragActive,
  } = useDropzone({
    onDrop: onDropAnswer,
    accept: { 'image/*': [] },
    maxSize: 500000,
    multiple: false,
  });

  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const questionId = question.questionId || question.id;
      if (!questionId) {
        console.error('Question ID is missing');
        return;
      }

      const updatedQuestion: Question = {
        ...question,
        question: questionText,
        answer: answerText,
        difficulty: difficulty,
        questionType: questionType,
        answerRange: answerRange,
      };

      await questionApi.updateQuestion(updatedQuestion);

      // If we have new images, upload them
      if (qFile || aFile) {
        await questionApi.uploadQuestionPhotos(
          questionId,
          qFile || new File([], 'empty'),
          aFile || new File([], 'empty')
        );
        if (qFile) {
          updatedQuestion.questionThumbnailUrl = questionImagePreview;
        }
        if (aFile) {
          updatedQuestion.answerThumbnailUrl = answerImagePreview;
        }
      }

      onUpdate(updatedQuestion);
      onCancel();
    } catch (error) {
      console.error('Failed to update question:', error);
      alert('Failed to update question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Theme-based styles
  const modalBgColor = theme === 'light' ? 'bg-white' : 'bg-gray-800';
  const borderColor = theme === 'light' ? 'border-gray-200' : 'border-gray-700';
  const textColor = theme === 'light' ? 'text-gray-800' : 'text-gray-200';
  const labelColor = theme === 'light' ? 'text-gray-700' : 'text-gray-200';
  const inputBgColor = theme === 'light' ? 'bg-gray-50' : 'bg-gray-700';
  const inputBorderColor = theme === 'light' ? 'border-gray-300' : 'border-gray-600';
  const secondaryBtnBgColor = theme === 'light' ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-700 hover:bg-gray-600';
  const secondaryBtnTextColor = theme === 'light' ? 'text-gray-700' : 'text-gray-200';
  const secondaryBtnBorderColor = theme === 'light' ? 'border-gray-300' : 'border-gray-600';
  
  const accentColor = theme === 'light' ? '[#3b82f6]' : '[#3b82f6]';
  const accentHoverColor = theme === 'light' ? '[#2563eb]' : '[#2563eb]';
  
  const getDragBorderColor = (isActive: boolean) => {
    if (isActive) {
      return theme === 'light' 
        ? 'border-[#3b82f6] bg-blue-50' 
        : 'border-[#3b82f6] bg-blue-900/20';
    }
    return theme === 'light' 
      ? 'border-gray-300 hover:border-blue-300' 
      : 'border-gray-600 hover:border-blue-500/50';
  };

  // --- The modal JSX ---
  const modalMarkup = (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${modalBgColor} rounded-lg w-full max-w-lg p-6 m-4 border ${borderColor}
                   max-h-[90vh] overflow-y-auto`}
      >
        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-[#3b82f6]">تعديل السؤال</h2>
          <Button
            variant="secondary"
            onClick={onCancel}
            className={`p-1 ${theme === 'light' ? 'text-gray-500 hover:text-gray-700' : 'text-gray-300 hover:text-gray-100'} ${secondaryBtnBgColor} border ${secondaryBtnBorderColor}`}
          >
            إغلاق
          </Button>
        </div>

        {/* BODY */}
        <div className="space-y-4">
          {/* Question Field */}
          <div>
            <label className={`block text-sm font-medium ${labelColor} mb-1`}>نص السؤال</label>
            <textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              className={`w-full ${inputBgColor} rounded-lg p-3 text-sm ${textColor} border ${inputBorderColor} 
                         focus:outline-none focus:ring-2 focus:ring-${accentColor} resize-none`}
              rows={4}
              disabled={isSubmitting}
            />
          </div>

          {/* Answer Field */}
          <div>
            <label className={`block text-sm font-medium ${labelColor} mb-1`}>الإجابة</label>
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              className={`w-full ${inputBgColor} rounded-lg p-3 text-sm ${textColor} border ${inputBorderColor} 
                         focus:outline-none focus:ring-2 focus:ring-${accentColor} resize-none`}
              rows={4}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium ${labelColor} mb-1`}>نطاق الخطأ المسموح به</label>
            <input
              type="number"
              placeholder="أدخل نطاق الخطأ المسموح به (اختياري)"
              value={answerRange || ''}
              onChange={(e) => setAnswerRange(e.target.value ? Number(e.target.value) : undefined)}
              className={`w-full ${inputBgColor} rounded-lg p-3 text-sm ${textColor} border ${inputBorderColor} 
                         focus:outline-none focus:ring-2 focus:ring-${accentColor} focus:border-transparent
                         [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              min="0"
              step="0.1"
              disabled={isSubmitting}
            />
            <p className={`text-xs mt-1 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`}>
              استخدم هذا الحقل فقط إذا كانت الإجابة رقماً وتريد السماح بنطاق خطأ
            </p>
          </div>

          {/* Difficulty */}
          <div>
            <label className={`block text-sm font-medium ${labelColor} mb-1`}>الصعوبة</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as Question['difficulty'])}
              className={`w-full ${inputBgColor} rounded-lg p-2 text-sm ${textColor} border ${inputBorderColor} 
                         focus:outline-none focus:ring-2 focus:ring-${accentColor}`}
              disabled={isSubmitting}
            >
              <option value="EASY">سهل</option>
              <option value="MEDIUM">متوسط</option>
              <option value="HARD">صعب</option>
            </select>
          </div>

          {/* Question Type */}
          <div>
            <label className={`block text-sm font-medium ${labelColor} mb-1`}>نوع السؤال</label>
            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value as Question['questionType'])}
              className={`w-full ${inputBgColor} rounded-lg p-2 text-sm ${textColor} border ${inputBorderColor} 
                         focus:outline-none focus:ring-2 focus:ring-${accentColor}`}
              disabled={isSubmitting}
            >
              <option value={QuestionType.FREE}>مجاني</option>
              <option value={QuestionType.PAYED}>مدفوع</option>
            </select>
          </div>

          {/* Image Upload */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              {...getQuestionRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${getDragBorderColor(isQuestionDragActive)} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getQuestionInputProps()} disabled={isSubmitting} />
              {questionImagePreview ? (
                <div className="relative aspect-video rounded-lg overflow-hidden">
                  <img
                    src={questionImagePreview}
                    alt="معاينة السؤال"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-2">
                  <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'} text-center`}>
                    اسحب صورة السؤال أو انقر للتحميل
                  </p>
                </div>
              )}
            </div>

            <div
              {...getAnswerRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${getDragBorderColor(isAnswerDragActive)} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getAnswerInputProps()} disabled={isSubmitting} />
              {answerImagePreview ? (
                <div className="relative aspect-video rounded-lg overflow-hidden">
                  <img
                    src={answerImagePreview}
                    alt="معاينة الإجابة"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-2">
                  <p className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'} text-center`}>
                    اسحب صورة الإجابة أو انقر للتحميل
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            variant="secondary"
            onClick={onCancel}
            className={`px-4 py-2 ${secondaryBtnBgColor} ${secondaryBtnTextColor} border ${secondaryBtnBorderColor}`}
            disabled={isSubmitting}
          >
            إلغاء
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            className={`px-4 py-2 bg-${accentColor} text-white hover:bg-${accentHoverColor} border border-${accentHoverColor} ${theme === 'dark' ? `border-${accentColor}` : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                جاري الحفظ...
              </span>
            ) : (
              'حفظ التغييرات'
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );

  // This keeps it from being clipped or constrained by parent components.
  return ReactDOM.createPortal(modalMarkup, document.getElementById('modal-root') || document.body);
};

export default EditQuestionModal;
